import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, History, Loader, Minimize2, FileText, ExternalLink } from 'lucide-react';
import StreamingContent from './StreamingContent';

const ChatPanel = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API}/api/chat/sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(Array.isArray(data) ? data : (data.sessions || []));
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const createSession = async (firstMessage) => {
    const response = await fetch(`${API}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title: firstMessage.slice(0, 50) })
    });
    if (!response.ok) throw new Error('Failed to create session');
    const data = await response.json();
    return data.id;
  };

  const loadSession = async (selectedSessionId) => {
    try {
      const response = await fetch(`${API}/api/chat/sessions/${selectedSessionId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const msgs = (data.messages || []).map(m => ({
          text: m.content,
          sender: m.role === 'user' ? 'user' : 'ai',
          sources: m.sources || []
        }));
        setMessages(msgs);
        setSessionId(selectedSessionId);
        setShowSessions(false);
      }
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMsg = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { text: userMsg, sender: 'user' }]);
    setLoading(true);

    try {
      // Create a session on first message if none exists
      let activeSessionId = sessionId;
      if (!activeSessionId) {
        activeSessionId = await createSession(userMsg);
        setSessionId(activeSessionId);
        fetchSessions();
      }

      const response = await fetch(`${API}/api/chat/sessions/${activeSessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg })
      });

      if (response.ok) {
        const data = await response.json();
        const aiText = data.ai_message?.content || data.content || data.response || '';
        setMessages(prev => [...prev, { text: aiText, sender: 'ai', sources: data.ai_message?.sources || [] }]);
      } else {
        setMessages(prev => [...prev, { text: 'Sorry, I encountered an error. Please try again.', sender: 'ai' }]);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { text: 'Sorry, I encountered an error. Please try again.', sender: 'ai' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowSessions(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-6 bottom-6 bg-ugmonk-yellow text-black p-4 rounded-full shadow-lg hover:shadow-xl transition-all z-50"
        title="Chat with VAIA AI"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed right-6 bottom-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200">
      <div className="bg-ugmonk-yellow text-black p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} />
          <div>
            <h3 className="font-semibold text-sm">VAIA AI</h3>
            <p className="text-xs opacity-90">{user.role === 'user' ? 'Client' : user.role === 'rm' ? 'RM' : 'Admin'} Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowSessions(!showSessions)} className="p-1.5 hover:bg-black/10 rounded transition-colors" title="History">
            <History size={16} />
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-black/10 rounded transition-colors" title="Close">
            <Minimize2 size={16} />
          </button>
        </div>
      </div>

      {showSessions && (
        <div className="bg-gray-50 border-b border-gray-200 max-h-40 overflow-y-auto">
          <div className="p-3">
            <button onClick={startNewChat} className="w-full mb-2 px-3 py-2 bg-ugmonk-dark text-white rounded-lg hover:bg-ugmonk-darker text-xs font-medium">
              + New Chat
            </button>
            <div className="space-y-1">
              {sessions.slice(0, 5).map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => loadSession(session.session_id)}
                  className={`w-full text-left px-2 py-1.5 rounded-lg text-xs ${session.session_id === sessionId ? 'bg-[#fffdf0] text-ugmonk-dark border border-ugmonk-border' : 'hover:bg-[#fffdf0] text-ugmonk-grey'}`}
                >
                  <div className="font-medium truncate">{session.preview}</div>
                  <div className="text-[10px] opacity-70">{session.message_count} msgs</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-gray-50 to-white">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-3 bg-[#fffdf0] rounded-full flex items-center justify-center">
              <MessageCircle size={32} className="text-ugmonk-dark" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2 text-sm">Hi! How can I help?</h4>
            <p className="text-xs text-gray-600 mb-4">Ask me anything about your clients and their portfolios.</p>
            <div className="space-y-2">
              <button onClick={() => setInputMessage("Which of my clients are most overdue for a portfolio review?")} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-ugmonk-yellow text-xs text-gray-700">
                📋 Which clients are overdue for a review?
              </button>
              <button onClick={() => setInputMessage("Which clients have high equity exposure and may need rebalancing?")} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-ugmonk-yellow text-xs text-gray-700">
                ⚖️ Clients needing rebalancing?
              </button>
              <button onClick={() => setInputMessage("Summarise the risk profiles of all my clients")} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-ugmonk-yellow text-xs text-gray-700">
                🧾 Summarise my clients' risk profiles
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 ${msg.sender === 'user' ? 'bg-ugmonk-dark text-white' : 'bg-white border border-ugmonk-border text-ugmonk-dark shadow-sm'}`}>
              {msg.sender === 'ai' ? (
                <>
                  <StreamingContent content={msg.text} isStreaming={false} />
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Sources</p>
                      {msg.sources.map((src, i) =>
                        src.type === 'web' ? (
                          <a key={i} href={src.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start gap-2 p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group">
                            <img src={`https://www.google.com/s2/favicons?domain=${(() => { try { return new URL(src.url).hostname } catch { return '' } })()}&sz=16`}
                              alt="" className="w-4 h-4 mt-0.5 flex-shrink-0 rounded" onError={e => { e.target.style.display='none' }} />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-800 truncate">{src.title || src.url}</p>
                              {src.description && <p className="text-[10px] text-gray-500 line-clamp-1">{src.description}</p>}
                            </div>
                            <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                          </a>
                        ) : (
                          <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-[#fffdf0]">
                            <FileText className="w-3.5 h-3.5 text-ugmonk-dark flex-shrink-0" />
                            <span className="text-xs text-gray-800 truncate">{src.filename || src.title || 'File'}</span>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader className="animate-spin text-ugmonk-yellow" size={14} />
                <span className="text-xs text-gray-600">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 bg-white border-t border-gray-200 rounded-b-2xl">
        <div className="flex gap-2 items-end">
          <textarea value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask anything..." className="flex-1 px-3 py-2 border border-ugmonk-border rounded-lg focus:ring-2 focus:ring-ugmonk-yellow focus:border-transparent resize-none text-sm" rows="2" disabled={loading} />
          <button onClick={sendMessage} disabled={loading || !inputMessage.trim()} className="px-3 py-2 bg-ugmonk-yellow text-black rounded-lg hover:bg-[#f5cc6a] hover:shadow-lg transition-all disabled:opacity-50 h-10 flex items-center justify-center">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
